<template>
  <div id="app-calendar" v-if="$withPolicy('application', 'calendar')">

    <main>
      <header>
        <div class="text-nowrap">
          <span class="month-year">
            <span><strong>{{ month }}</strong> / {{ this.query.year }}</span>
          </span>
          <span class="ml-2">
            <chevron-left-icon class="chevron" @click="prevMonth()"/>
            <chevron-right-icon class="chevron" @click="nextMonth()"/>
          </span>
        </div>

        <div class="header-actions">
          <div class="keyboard-shortcuts" title="Atalhos: N=Novo evento, T=Hoje, ←→=Navegar meses, /=Buscar, Esc=Toggle sidebar">
            <small class="text-muted">Atalhos disponíveis</small>
          </div>
          <div class="action-buttons">
            <button @click="exportToICS" class="btn btn-outline-primary btn-sm mr-2 no-print" title="Exportar para calendário">
              <calendar-icon size="16" class="mr-1"/>
              Exportar
            </button>
            <button @click="triggerFileUpload" class="btn btn-outline-success btn-sm mr-2 no-print" title="Importar do calendário">
              <upload-icon size="16" class="mr-1"/>
              Importar
            </button>
            <input ref="fileInput" type="file" accept=".ics" @change="handleFileUpload" style="display: none">
            <div v-if="currentMonth === query.month && currentYear === query.year">
              <button @click="openModal({date: new Date()})" class="btn btn-primary btn-xl" >Novo evento</button>
            </div>
            <div v-else>
              <a href='#' @click.prevent="goToCurrentDate()" class="pr-2"> Hoje </a>
              <button @click="openModal({date: new Date()})" class="btn btn-primary btn-xl">Novo evento</button>
            </div>
          </div>
        </div>

      </header>

      <TabContainer startHeight="calc(100vh - 140px)">
        <Tab name="Mês" :startActive="true">
          <div class="calendar-table">
            <div class="calendar-weekdays row-week">
              <div class="day-week-header">Dom</div>
              <div class="day-week-header">Seg</div>
              <div class="day-week-header">Ter</div>
              <div class="day-week-header">Qua</div>
              <div class="day-week-header">Qui</div>
              <div class="day-week-header">Sex</div>
              <div class="day-week-header">Sáb</div>
            </div>

        <div class="row-week" v-for="(week, index) in weeks" :key="index">

          <div class="day-week" v-for="day in week" :key="day.date">

            <Day :date="getDate(day)" @clickedDay="openModal({date: $event})"
                 :calendars="eventsByDay(day, week)"
                 @clickCalendar="openModal({calendar: $event})"
                 @event-dropped="onEventDropped"
                 :current-month="query.month"/>
          </div>

            </div>
          </div>
        </Tab>

        <Tab name="Semana">
          <div class="week-view">
            <div class="week-header">
              <div class="week-navigation">
                <button @click="prevWeek" class="btn btn-outline-primary btn-sm">
                  <chevron-left-icon size="16"/>
                </button>
                <h4>{{ weekRange }}</h4>
                <button @click="nextWeek" class="btn btn-outline-primary btn-sm">
                  <chevron-right-icon size="16"/>
                </button>
              </div>
            </div>
            
            <div class="week-grid">
              <div class="time-column">
                <div class="time-header"></div>
                <div v-for="hour in hours" :key="hour" class="time-slot">
                  {{ hour }}:00
                </div>
              </div>
              
              <div v-for="day in weekDays" :key="day.date.getTime()" class="day-column">
                <div class="day-header">
                  <div class="day-name">{{ day.dayName }}</div>
                  <div class="day-number" :class="{ 'today': day.isToday }">{{ day.dayNumber }}</div>
                </div>
                
                <div v-for="hour in hours" :key="hour" class="day-hour-slot" @click="createEventAt(day.date, hour)">
                  <div v-for="event in getEventsForDayHour(day.date, hour)" 
                       :key="`${event.id}-${hour}`" 
                       class="week-event" 
                       :style="{ 
                         background: getEventColor(event),
                         height: getEventHeightForHour(event, hour) + 'px'
                       }"
                       @click.stop="openModal({calendar: event})">
                    {{ event.title }}
                    <div class="event-time-in-slot">{{ getEventTimeInHour(event, hour) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Tab>

        <Tab name="Dia">
          <div class="day-view">
            <div class="day-header-section">
              <div class="day-navigation">
                <button @click="prevDay" class="btn btn-outline-primary btn-sm">
                  <chevron-left-icon size="16"/>
                </button>
                <h4>{{ currentDayFormatted }}</h4>
                <button @click="nextDay" class="btn btn-outline-primary btn-sm">
                  <chevron-right-icon size="16"/>
                </button>
              </div>
            </div>
            
            <div class="day-timeline">
              <div class="timeline-hours">
                <div v-for="hour in detailedHours" :key="hour" class="hour-block">
                  {{ hour }}:00
                </div>
              </div>
              
              <div class="timeline-content">
                <div v-for="hour in detailedHours" :key="hour" class="hour-slot" @click="createEventAt(selectedDay, hour)">
                  <div v-for="event in getEventsForDayHour(selectedDay, hour)" 
                       :key="`${event.id}-${hour}`"
                       class="day-event" 
                       :style="{ 
                         background: getEventColor(event),
                         height: getEventHeightForHour(event, hour) + 'px'
                       }"
                       @click.stop="openModal({calendar: event})">
                    <div class="event-time">{{ getEventTimeInHour(event, hour) }}</div>
                    <div class="event-title">{{ event.title }}</div>
                    <div class="event-desc" v-if="event.description">{{ event.description }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Tab>

        <Tab name="Lista">
          <div class="list-view">
            <div class="events-list">
              <h3 v-if="calendars.length === 0" class="text-center py-4">
                Nenhum evento para este período
              </h3>
              <div v-else>
                <div v-for="calendar in calendars" :key="calendar.id" class="event-list-item" @click="openModal({calendar})">
                  <div class="event-list-date">
                    <strong>{{ calendar.startDate | date }}</strong>
                    <span class="ml-2">{{ calendar.startDate | time }} - {{ calendar.endDate | time }}</span>
                  </div>
                  <div class="event-list-title">
                    <strong>{{ calendar.title }}</strong>
                  </div>
                  <div class="event-list-desc">
                    {{ calendar.description }}
                  </div>
                  <hr>
                </div>
              </div>
            </div>
          </div>
        </Tab>

        <Tab name="Eventos">
          <div class="events-tab">
            <!-- Filtros e Busca -->
            <div class="filters-section">
              <div class="search-box">
                <input 
                  v-model="searchQuery" 
                  type="text" 
                  class="form-control" 
                  placeholder="Buscar eventos..."
                  @input="onSearchInput"
                />
              </div>
              
              <div class="filters-row">
                <select v-model="selectedFilter" class="form-control">
                  <option value="all">Todos os eventos</option>
                  <option value="public">Eventos públicos</option>
                  <option value="private">Eventos privados</option>
                  <option value="global">Eventos globais</option>
                </select>
              </div>
            </div>
            
            <div class="event-container" v-if="filteredEvents.length > 0">
              <div class="event-card" v-for="calendar in filteredUpcomingEvents" :key="calendar.id" @click="openModal({calendar})">
                <div class="event-card-header">
                  <div class="event-card-title">
                    <calendar-icon size="16" class="mr-2"/>
                    <strong>{{ calendar.title }}</strong>
                  </div>
                  <span class="event-card-badge" :class="getEventTypeClass(calendar)">
                    {{ getEventTypeLabel(calendar) }}
                  </span>
                </div>
                <div class="event-card-time">
                  <clock-icon size="14" class="mr-1"/>
                  <span>{{ calendar.startDate | datetime }}</span>
                </div>
                <div class="event-card-desc" v-if="calendar.description">
                  {{ calendar.description | truncate(60) }}
                </div>
                <div class="event-card-footer" v-if="calendar.type === 'private'">
                  <users-icon size="14" class="mr-1"/>
                  <span>Evento privado</span>
                </div>
              </div>
            </div>

            <div v-else class="no-events">
              <div class="text-center">
                <span v-if="searchQuery">Nenhum evento encontrado para "<strong>{{ searchQuery }}</strong>"</span>
                <span v-else>Nenhum evento encontrado</span>
                <div class="pt-4">
                  <button @click="openModal({date: new Date()})" class="btn btn-primary btn-xl">Criar novo evento</button>
                </div>
              </div>
            </div>
          </div>
        </Tab>
      </TabContainer>

    </main>

    <Modal v-model="showModalCalendar">

      <template v-if="isGlobal">
        <div slot="header">
          Evento
        </div>

        <div class="form-group">
          <label>Título</label>
          <br>
          <label>
            <strong>{{ currentCalendar.title}} </strong>
          </label>
        </div>

        <div class="form-group">
          <label>Descrição</label>
          <br>
          <label>
            <strong>{{ currentCalendar.description }} </strong>
          </label>
        </div>

        <div class="form-group">
          <label for="start-date">Data e hora inicial</label>
          <br>
          <label>
            <strong>{{ currentCalendar.startDate | datetime }} </strong>
          </label>
        </div>

        <div class="form-group">
          <label for="start-hour">Date e hora final</label>
          <br>
          <label>
            <strong>{{ currentCalendar.endDate | datetime }} </strong>
          </label>
        </div>

      </template>

      <template v-else>

        <div slot="header">
          {{ currentCalendar.id ? 'Editar evento' : 'Novo evento' }}
        </div>

        <div class="form-group">
          <label for="title">Título</label>

          <input v-model="currentCalendar.title" class="form-control" id="title">
        </div>

        <div class="form-group">
          <label for="description">Descrição</label>
          <textarea v-model="currentCalendar.description" class="form-control" id="description" rows="8"/>
        </div>

        <div class="row">
          <div class="form-group col-sm-3">
            <label for="start-date">Início</label>
            <input v-model="currentCalendar.startDate" type="date" class="form-control" id="start-date"/>
          </div>

          <div class="form-group col-sm-3">
            <label for="start-hour">Horário</label>
            <select v-model="currentCalendar.startHour" class="form-control" id="start-hour">
              <option :value="option" v-for="option in optionsHour" :key="option">
                {{ option }}
              </option>
            </select>
          </div>
        </div>

        <div class="row">
          <div class="form-group col-sm-3">
            <label for="end-date">Fim</label>
            <input v-model="currentCalendar.endDate" type="date" class="form-control" id="end-date"/>
          </div>

          <div class="form-group col-sm-3">
            <label for="end-hour">Horário</label>
            <select v-model="currentCalendar.endHour" class="form-control" id="end-hour">
              <option :value="option" v-for="option in optionsHour" :key="option">
                {{ option }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <BkpsCheckBox v-model="currentCalendar.sendEmail" class="col-sm-3">
            <span>Notificar por email</span>
          </BkpsCheckBox>

          <BkpsCheckBox v-model="setPrivateCalendar" class="col-sm-3">
            <span>Privado</span>
          </BkpsCheckBox>

          <Select2 v-show="setPrivateCalendar" v-model="currentCalendar.userIds" :options="optionsUser"/>

        </div>

        <div class="form-group float-right">
          <button v-if="currentCalendar.id" @click="deleteCalendar()" class="btn btn-outline-red btn-md mr-2">
            Excluir
          </button>
          <button v-else @click="showModalCalendar=false" class="btn btn-outline btn-md mr-2">
            Cancelar
          </button>
          <button @click="saveCalendar()" :disabled="enableSaveButton" class="btn btn-primary btn-md">Concluir</button>
        </div>

      </template>

    </Modal>

  </div>

  <NotFound v-else/>

</template>

<script>
  import BkpsCheckBox from '@/components/shares/specialTag/BkpsCheckBox'
  import Day from '@/components/calendar/Day'
  import EntityProxy from '@/models/EntityProxy'
  import GetUser from '@/mixins/GetUser'
  import GetAccount from '@/mixins/GetAccount.vue';
  import Modal from '@/components/shares/specialTag/Modal'
  import ModelMapper from '@/mixins/ModelMapper'
  import NotFound from '@/components/shares/NotFound'
  import Select2 from '@/components/shares/Select2'
  import TabContainer from '@/components/shares/TabContainer'
  import Tab from '@/components/shares/Tab'
  import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, UploadIcon, UsersIcon } from 'vue-feather-icons'

  export default {
    components: { BkpsCheckBox, CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, Day, Modal, NotFound, Select2, Tab, TabContainer, UploadIcon, UsersIcon },
    computed: {
      calendars() {
        return this.getModels || []
      },
      currentMonth(){
        let date = new Date()
        let month = date.getMonth() + 1
        return month
      },
      currentYear(){
        let date = new Date()
        let year = date.getFullYear()
        return year
      },
      days() {
        let days = []
        if (this.meta.currentMonth)
          days = this.meta.currentMonth.map(d => new Date(d.date))
        return days
      },
      enableSaveButton() {
        return !this.currentCalendar.title
      },
      isGlobal() {
        return this.currentCalendar.type === 'global'
      },
      isPrivate() {
        return this.currentCalendar.type === 'private'
      },
      month() {
        return ['Janeiro', 'Fevereiro', 'Março', 'Abril',
          'Maio', 'Junho', 'Julho', 'Agosto',
          'Setembro', 'Outubro', 'Novembro', 'Dezembro'][this.query.month -1]
      },
      optionsHour() {
        let hours = [...Array(24).keys()].map(n => `${n}`.padStart(2, '0'))
        let minutes = ['00', '30']
        let options = []
        hours.forEach(h => {
          minutes.forEach(m => {
            options.push(`${h}:${m}`)
          })
        })
        return options
      },
      weeks() {
        let days = this.meta.currentMonth || []
        if (days.length === 0)
          return days

        let weeks = []
        let week = []
        let numberWeek = 0
        days.forEach(day => {
          if (numberWeek === day.numberWeek)
            week.push(day)
          else {
            numberWeek = day.numberWeek
            week = [day]
            weeks.push(week)
          }
        })

        if (weeks.some(w => w.length < 7)) {
          let newWeek
          if (weeks[0].length < 7) {
            newWeek = weeks[0].concat(weeks[1])
            weeks.splice(0, 2, newWeek)
          } else {
            let size = weeks.length
            newWeek = weeks[size - 2].concat(weeks[size - 1])
            weeks.splice(size - 2, 2, newWeek)
          }
        }

        return weeks
      },
      upcomingEvents() {
        // Filtrar apenas eventos futuros ou em andamento
        const now = new Date()
        return this.calendars
          .filter(event => new Date(event.endDate) >= now)
          .slice(0, 5) // Limitar a 5 eventos
      },
      filteredEvents() {
        let events = this.calendars
        
        // Filtro por tipo
        if (this.selectedFilter !== 'all') {
          events = events.filter(event => event.type === this.selectedFilter)
        }
        
        // Filtro por busca
        if (this.searchQuery.trim()) {
          const query = this.searchQuery.toLowerCase()
          events = events.filter(event => 
            event.title.toLowerCase().includes(query) ||
            (event.description && event.description.toLowerCase().includes(query))
          )
        }
        
        return events
      },
      filteredUpcomingEvents() {
        const now = new Date()
        return this.filteredEvents
          .filter(event => new Date(event.endDate) >= now)
          .slice(0, 5)
      },
      weekRange() {
        const start = new Date(this.selectedWeekStart)
        const end = new Date(start)
        end.setDate(end.getDate() + 6)
        
        return `${start.getDate()}/${start.getMonth() + 1} - ${end.getDate()}/${end.getMonth() + 1}/${end.getFullYear()}`
      },
      weekDays() {
        const days = []
        for (let i = 0; i < 7; i++) {
          const day = new Date(this.selectedWeekStart)
          day.setDate(this.selectedWeekStart.getDate() + i)
          days.push({
            date: day,
            dayName: day.toLocaleDateString('pt-BR', { weekday: 'short' }),
            dayNumber: day.getDate(),
            isToday: this.isToday(day)
          })
        }
        return days
      },
      hours() {
        return Array.from({ length: 12 }, (_, i) => i + 8) // 8h às 19h
      },
      detailedHours() {
        return Array.from({ length: 24 }, (_, i) => i) // 0h às 23h
      },
      detailedTimeSlots() {
        const slots = []
        for (let hour = 0; hour < 24; hour++) {
          slots.push({ hour, minute: 0, display: `${hour.toString().padStart(2, '0')}:00` })
          slots.push({ hour, minute: 30, display: `${hour.toString().padStart(2, '0')}:30` })
        }
        return slots
      },
      currentDayFormatted() {
        return this.selectedDay.toLocaleDateString('pt-BR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })
      }
    },
    created() {
      let date = new Date()
      this.$set(this.query, 'month', date.getMonth() + 1)
      this.$set(this.query, 'year', date.getFullYear())
      this.order.push('startDate', 'asc')
      this.params.perPage = 1000

      this.asyncTemplateSelect({
        after: () => this.createOptionsUser()
      })

      // Adicionar listeners de teclado
      window.addEventListener('keydown', this.handleKeyboard)

      // Inicializar semana atual (começando no domingo)
      this.initializeWeek()
    },
    beforeDestroy() {
      window.removeEventListener('keydown', this.handleKeyboard)
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
    },
    data() {
      return {
        currentCalendar: {},
        mapRows: {},
        nameModel: 'Calendar',
        optionsUser: [],
        setPrivateCalendar: false,
        showModalCalendar: false,
        searchQuery: '',
        selectedFilter: 'all',
        searchTimeout: null,
        selectedWeekStart: new Date(),
        selectedDay: new Date(),
        dayNames: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
      }
    },
    methods: {
      createOptionsUser() {
        const params =  {
          fields: ['id', 'name', 'email'],
          meta: false,
          per_page: 1000
        }

        this.axios.get('users', { params })
          .then(response => response.data)
          .then(users => this.$parse.toCamelcase(users))
          .then(users => this.optionsUser = users.map(u => ({ label: `${u.name} - ${u.email}`, value: u.id})))
      },
      isToday(date) {
        const today = new Date()
        return date.getDate() === today.getDate() &&
               date.getMonth() === today.getMonth() &&
               date.getFullYear() === today.getFullYear()
      },
      deleteCalendar() {
        this.asyncTemplateDelete(this.currentCalendar, {
          after: () => {
            this.showModalCalendar = false
            this.$notify.add('Event excluído.')
            let index = this.calendars.findIndex(c => c.id === this.currentCalendar.id)
            this.calendars.splice(index, 1)
          }
        })
      },
      eventsByDay(objDay) {
        let day = this.getDate(objDay).getUTCDate()
        let month = this.query.month
        let year = this.query.year

        let endDate = new Date(year, month -1, day)
        endDate.setHours(23, 59, 59, 999)

        let startDate = new Date(year, month -1, day)
        startDate.setHours(0, 0, 0, 0)

        return this.calendars.filter(e => new Date(e.startDate) <= endDate && new Date(e.endDate) >= startDate)
      },
      find() {
        this.asyncTemplateSelect()
      },
      getDate(obj) {
        return new Date(obj.date)
      },
      goToCurrentDate(){
        let month = this.currentMonth
        let year = this.currentYear
        this.query.month = month
        this.query.year = year
        this.find()
      },
      nextMonth() {
        this.query.month = this.query.month + 1
        if (this.query.month > 12) {
          this.query.year++
          this.query.month = 1
        }
        this.find()
      },
      openModal({ date, calendar } = {}) {
        if (date) {
          let dateString = date.toISOString().split('T')[0]
          this.setPrivateCalendar = false
          this.currentCalendar = EntityProxy.build({
            sendEmail: false,
            title: '',
            startDate: dateString,
            endDate: dateString,
            startHour: '08:00',
            endHour: '09:30',
            userIds: [],
            description: '',
          })
        } else if (calendar && calendar.type === 'global') {
          this.currentCalendar = EntityProxy.clone(calendar)
        } else if (calendar) {
          calendar = EntityProxy.clone(calendar)
          let startDate = calendar.startDate
          calendar.startDate = startDate.substring(0, 10)
          calendar.startHour = startDate.substring(11, 16)

          let endDate = calendar.endDate
          calendar.endDate = endDate.substring(0, 10)
          calendar.endHour = endDate.substring(11, 16)

          this.setPrivateCalendar = calendar.type === 'private'
          this.currentCalendar = calendar
        }

        this.showModalCalendar = true
      },
      prevMonth() {
        this.query.month = this.query.month - 1
        if (this.query.month < 1) {
          this.query.year--
          this.query.month = 12
        }
        this.find()
      },
      saveCalendar() {
        let calendar = {...this.currentCalendar}
        calendar.startDate =
          this.currentCalendar.startDate + 'T' +
          this.currentCalendar.startHour + ':00'

        calendar.endDate =
          this.currentCalendar.endDate + 'T' +
          this.currentCalendar.endHour + ':00'

        calendar.type = this.setPrivateCalendar ? 'private' : 'public'

        this.asyncTemplateSave(calendar, {
          after: () => {
            this.$notify.add('Evento salvo com sucesso')
            this.showModalCalendar = false
            this.find()
          }
        })
      },
      getEventTypeClass(event) {
        switch (event.type) {
          case 'private': return 'badge-private'
          case 'global': return 'badge-global'
          default: return 'badge-public'
        }
      },
      getEventTypeLabel(event) {
        switch (event.type) {
          case 'private': return 'Privado'
          case 'global': return 'Global'
          default: return 'Público'
        }
      },
      onSearchInput() {
        // Debounce para evitar muitas buscas
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout)
        }
      },
      handleKeyboard(event) {
        // Ignorar se estiver digitando em um input
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) {
          return
        }

        // Ignorar se modal estiver aberto
        if (this.showModalCalendar) {
          return
        }

        switch (event.key.toLowerCase()) {
          case 'n':
            event.preventDefault()
            this.openModal({ date: new Date() })
            break
          case 't':
            event.preventDefault()
            this.goToCurrentDate()
            break
          case 'arrowleft':
            event.preventDefault()
            this.prevMonth()
            break
          case 'arrowright':
            event.preventDefault()
            this.nextMonth()
            break
          case 'escape':
            // Fechar modal se estiver aberto
            if (this.showModalCalendar) {
              this.showModalCalendar = false
            }
            break
          case '/': {
            event.preventDefault()
            // Focar no input de busca
            const searchInput = document.querySelector('.search-box input')
            if (searchInput) {
              searchInput.focus()
            }
            break
          }
        }
      },
      async onEventDropped({ eventId, newDate }) {
        try {
          // Encontrar o evento original
          const eventToUpdate = this.calendars.find(event => event.id === eventId)
          if (!eventToUpdate) return

          // Calcular diferença em dias
          const originalDate = new Date(eventToUpdate.startDate)
          const targetDate = new Date(newDate)
          const timeDiff = targetDate.getTime() - originalDate.getTime()
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))

          // Atualizar datas do evento
          const newStartDate = new Date(eventToUpdate.startDate)
          newStartDate.setDate(newStartDate.getDate() + daysDiff)
          
          const newEndDate = new Date(eventToUpdate.endDate)
          newEndDate.setDate(newEndDate.getDate() + daysDiff)

          const updatedEvent = {
            ...eventToUpdate,
            startDate: newStartDate.toISOString(),
            endDate: newEndDate.toISOString()
          }

          // Salvar via API
          await this.asyncTemplateSave(updatedEvent, {
            after: () => {
              this.$notify.add('Evento movido com sucesso!')
              this.find()
            },
            error: () => {
              this.$notify.add('Erro ao mover evento', 'error')
            }
          })
        } catch (error) {
          console.error('Erro no drag & drop:', error)
          this.$notify.add('Erro ao mover evento', 'error')
        }
      },
      exportToICS() {
        if (this.calendars.length === 0) {
          this.$notify.add('Nenhum evento para exportar')
          return
        }

        const icsContent = this.generateICSContent()
        const blob = new Blob([icsContent], { type: 'text/calendar' })
        const url = URL.createObjectURL(blob)
        
        const link = document.createElement('a')
        link.href = url
        link.download = `eventos-${this.month.toLowerCase()}-${this.query.year}.ics`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        URL.revokeObjectURL(url)
        this.$notify.add('Eventos exportados com sucesso!')
      },
      generateICSContent() {
        const now = new Date()
        const timestamp = now.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
        
        let icsContent = [
          'BEGIN:VCALENDAR',
          'VERSION:2.0',
          'PRODID:-//BKPS Platform//Calendar//PT',
          'CALSCALE:GREGORIAN',
          'METHOD:PUBLISH'
        ]
        
        this.calendars.forEach(event => {
          const startDate = new Date(event.startDate)
          const endDate = new Date(event.endDate)
          
          const formatICSDate = (date) => {
            return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
          }
          
          icsContent.push(
            'BEGIN:VEVENT',
            `UID:${event.id}@bkps-platform.com`,
            `DTSTAMP:${timestamp}`,
            `DTSTART:${formatICSDate(startDate)}`,
            `DTEND:${formatICSDate(endDate)}`,
            `SUMMARY:${event.title}`,
            `DESCRIPTION:${event.description || ''}`,
            `STATUS:${event.type === 'global' ? 'CONFIRMED' : 'TENTATIVE'}`,
            `TRANSP:${event.type === 'private' ? 'CONFIDENTIAL' : 'OPAQUE'}`,
            'END:VEVENT'
          )
        })
        
        icsContent.push('END:VCALENDAR')
        
        return icsContent.join('\r\n')
      },
      triggerFileUpload() {
        this.$refs.fileInput.click()
      },
      handleFileUpload(event) {
        const file = event.target.files[0]
        if (!file) return

        if (!file.name.toLowerCase().endsWith('.ics')) {
          this.$notify.add('Por favor, selecione um arquivo .ics válido', 'error')
          return
        }

        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const icsContent = e.target.result
            this.parseICSFile(icsContent)
          } catch (error) {
            console.error('Erro ao ler arquivo:', error)
            this.$notify.add('Erro ao ler o arquivo .ics', 'error')
          }
        }
        reader.readAsText(file)
        
        // Limpar o input para permitir upload do mesmo arquivo novamente
        event.target.value = ''
      },
      parseICSFile(icsContent) {
        const lines = icsContent.split(/\r?\n/)
        const events = []
        let currentEvent = null
        let insideEvent = false

        for (let line of lines) {
          line = line.trim()
          
          if (line === 'BEGIN:VEVENT') {
            insideEvent = true
            currentEvent = {
              title: '',
              description: '',
              startDate: '',
              endDate: '',
              type: 'public'
            }
          } else if (line === 'END:VEVENT' && currentEvent) {
            if (currentEvent.title && currentEvent.startDate) {
              events.push(currentEvent)
            }
            currentEvent = null
            insideEvent = false
          } else if (insideEvent && currentEvent) {
            if (line.startsWith('SUMMARY:')) {
              currentEvent.title = line.substring(8).trim()
            } else if (line.startsWith('DESCRIPTION:')) {
              currentEvent.description = line.substring(12).trim()
            } else if (line.startsWith('DTSTART:')) {
              currentEvent.startDate = this.parseICSDate(line.substring(8).trim())
            } else if (line.startsWith('DTEND:')) {
              currentEvent.endDate = this.parseICSDate(line.substring(6).trim())
            }
          }
        }

        if (events.length > 0) {
          this.importEvents(events)
        } else {
          this.$notify.add('Nenhum evento válido encontrado no arquivo', 'warning')
        }
      },
      parseICSDate(icsDate) {
        // Formato ICS: 20231225T140000Z ou 20231225T140000
        const cleanDate = icsDate.replace(/[TZ]/g, '')
        const year = cleanDate.substring(0, 4)
        const month = cleanDate.substring(4, 6)
        const day = cleanDate.substring(6, 8)
        const hour = cleanDate.substring(8, 10) || '00'
        const minute = cleanDate.substring(10, 12) || '00'
        
        return `${year}-${month}-${day}T${hour}:${minute}:00`
      },
      importEvents(events) {
        const importPromises = events.map(event => {
          const formattedEvent = {
            title: event.title,
            description: event.description,
            startDate: event.startDate,
            endDate: event.endDate || event.startDate, // Se não tiver fim, usar início
            type: event.type,
            color: '#2d98da'
          }

          // Extrair data e hora separadamente (formato esperado pelo backend)
          const startDateTime = new Date(event.startDate)
          const endDateTime = new Date(event.endDate || event.startDate)
          
          formattedEvent.startDate = startDateTime.toISOString().split('T')[0]
          formattedEvent.endDate = endDateTime.toISOString().split('T')[0]
          formattedEvent.startHour = startDateTime.toTimeString().substring(0, 5)
          formattedEvent.endHour = endDateTime.toTimeString().substring(0, 5)

          return this.asyncTemplateSave(formattedEvent)
        })

        Promise.all(importPromises)
          .then(() => {
            this.$notify.add(`${events.length} evento(s) importado(s) com sucesso!`)
            this.find() // Recarregar eventos
          })
          .catch(error => {
            console.error('Erro ao importar eventos:', error)
            this.$notify.add('Erro ao importar alguns eventos', 'error')
          })
      },
      prevWeek() {
        this.selectedWeekStart.setDate(this.selectedWeekStart.getDate() - 7)
        this.$forceUpdate()
      },
      nextWeek() {
        this.selectedWeekStart.setDate(this.selectedWeekStart.getDate() + 7)
        this.$forceUpdate()
      },
      prevDay() {
        this.selectedDay.setDate(this.selectedDay.getDate() - 1)
        this.$forceUpdate()
      },
      nextDay() {
        this.selectedDay.setDate(this.selectedDay.getDate() + 1)
        this.$forceUpdate()
      },
      getEventsForDayHour(date, hour) {
        // Garantir que a data seja um objeto Date válido
        const targetDate = new Date(date)
        
        // Definir o início e fim da hora específica do dia
        const hourStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), hour, 0, 0, 0)
        const hourEnd = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), hour + 1, 0, 0, 0)
        
        return this.calendars.filter(event => {
          const eventStart = new Date(event.startDate)
          const eventEnd = new Date(event.endDate)
          
          // Verificar se o evento é nesta data específica
          const eventStartsInThisDay = eventStart.getFullYear() === targetDate.getFullYear() &&
                                       eventStart.getMonth() === targetDate.getMonth() &&
                                       eventStart.getDate() === targetDate.getDate()
          
          if (!eventStartsInThisDay) return false
          
          // Verificar se o evento ocorre durante esta hora (sobreposição de tempo)
          // Um evento aparece no slot se:
          // 1. Começa durante esta hora, OU
          // 2. Termina durante esta hora, OU  
          // 3. Começa antes desta hora e termina depois (atravessa toda a hora)
          return (eventStart >= hourStart && eventStart < hourEnd) ||
                 (eventEnd > hourStart && eventEnd <= hourEnd) ||
                 (eventStart < hourStart && eventEnd > hourEnd)
        })
      },
      getEventColor(event) {
        return event.color || '#3498db'
      },
      getEventTime(event) {
        const start = new Date(event.startDate)
        const end = new Date(event.endDate)
        return `${start.getHours()}:${start.getMinutes().toString().padStart(2, '0')} - ${end.getHours()}:${end.getMinutes().toString().padStart(2, '0')}`
      },
      getEventHeightForHour(event, hour) {
        const eventStart = new Date(event.startDate)
        const eventEnd = new Date(event.endDate)
        const hourStart = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate(), hour, 0, 0, 0)
        const hourEnd = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate(), hour + 1, 0, 0, 0)
        
        // Calcular quanto tempo o evento ocupa nesta hora específica
        const slotStart = Math.max(eventStart.getTime(), hourStart.getTime())
        const slotEnd = Math.min(eventEnd.getTime(), hourEnd.getTime())
        const durationInHour = slotEnd - slotStart
        const hourDuration = 60 * 60 * 1000 // 1 hora em millisegundos
        
        // Altura proporcional (slot tem 56px úteis, descontando 4px de margin/padding)
        const proportion = durationInHour / hourDuration
        return Math.max(15, Math.floor(56 * proportion)) // Mínimo 15px
      },
      getEventTimeInHour(event, hour) {
        const eventStart = new Date(event.startDate)
        const eventEnd = new Date(event.endDate)
        const hourStart = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate(), hour, 0, 0, 0)
        const hourEnd = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate(), hour + 1, 0, 0, 0)
        
        const slotStart = Math.max(eventStart.getTime(), hourStart.getTime())
        const slotEnd = Math.min(eventEnd.getTime(), hourEnd.getTime())
        
        const startTime = new Date(slotStart)
        const endTime = new Date(slotEnd)
        
        return `${startTime.getHours()}:${startTime.getMinutes().toString().padStart(2, '0')} - ${endTime.getHours()}:${endTime.getMinutes().toString().padStart(2, '0')}`
      },
      createEventAt(date, hour, minute = 0) {
        const eventDate = new Date(date)
        eventDate.setHours(hour, minute, 0, 0)
        this.openModal({ date: eventDate })
      },
      initializeWeek() {
        const today = new Date()
        const dayOfWeek = today.getDay()
        const sundayDate = new Date(today)
        sundayDate.setDate(today.getDate() - dayOfWeek)
        this.selectedWeekStart = sundayDate
      }
    },
    filters: {
      truncate(text, length) {
        if (!text) return ''
        if (text.length <= length) return text
        return text.substring(0, length) + '...'
      },
      time(datetime) {
        if (!datetime) return ''
        return new Date(datetime).toLocaleTimeString('pt-BR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      }
    },
    mixins: [ModelMapper, GetUser, GetAccount],
    name: 'Calendar'
  }
</script>

<style lang="scss" scoped>

  $width-main-header: 70px;
  $width-event-header: 22px;

  #app-calendar {
    height: 100%;
    color: var(--primary-text-color);
    position: relative;
  }

  .events-tab {
    padding: 20px;
    max-width: 800px;
    margin: 0 auto;

    .filters-section {
      padding: 20px;
      background: var(--white-bg);
      border: 1px solid var(--border-color);
      border-radius: 8px;
      margin-bottom: 20px;

      .search-box {
        margin-bottom: 15px;

        input {
          border-radius: 6px;
          border: 1px solid var(--border-color);
          background: var(--white-bg);
          color: var(--primary-text-color);
          width: 100%;
          
          &:focus {
            border-color: var(--primary-color);
            box-shadow: 0 0 0 2px rgba(45, 152, 218, 0.1);
          }

          &::placeholder {
            color: var(--secondary-text-color);
          }
        }
      }

      .filters-row {
        select {
          border-radius: 6px;
          border: 1px solid var(--border-color);
          background: var(--white-bg);
          color: var(--primary-text-color);
          width: 100%;

          &:focus {
            border-color: var(--primary-color);
            box-shadow: 0 0 0 2px rgba(45, 152, 218, 0.1);
          }
        }
      }
    }

    .event-container {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 20px;
    }

    .no-events {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 200px;
      color: var(--secondary-text-color);
    }
  }

  .event-card {
    background: var(--white-bg);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 18px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    width: 100%;
    box-sizing: border-box;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: translateY(-2px);
      border-color: var(--primary-color);
    }

    .event-card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;

      .event-card-title {
        display: flex;
        align-items: center;
        flex: 1;
        
        strong {
          color: var(--primary-text-color);
          font-size: 16px;
          line-height: 1.3;
        }

        svg {
          color: var(--primary-color);
          flex-shrink: 0;
          width: 16px;
          height: 16px;
        }
      }

      .event-card-badge {
        padding: 4px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        flex-shrink: 0;
        border: 1px solid;

        &.badge-public {
          background: rgba(25, 118, 210, 0.15);
          color: #1976d2;
          border-color: rgba(25, 118, 210, 0.3);
        }

        &.badge-private {
          background: rgba(245, 124, 0, 0.15);
          color: #f57c00;
          border-color: rgba(245, 124, 0, 0.3);
        }

        &.badge-global {
          background: rgba(56, 142, 60, 0.15);
          color: #388e3c;
          border-color: rgba(56, 142, 60, 0.3);
        }
      }

      [data-theme="dark"] & {
        .event-card-badge {
          &.badge-public {
            background: rgba(66, 165, 245, 0.2);
            color: #64b5f6;
            border-color: rgba(66, 165, 245, 0.4);
          }

          &.badge-private {
            background: rgba(255, 167, 38, 0.2);
            color: #ffb74d;
            border-color: rgba(255, 167, 38, 0.4);
          }

          &.badge-global {
            background: rgba(129, 199, 132, 0.2);
            color: #81c784;
            border-color: rgba(129, 199, 132, 0.4);
          }
        }
      }
    }

    .event-card-time {
      display: flex;
      align-items: center;
      color: var(--secondary-text-color);
      font-size: 14px;
      margin-bottom: 8px;

      svg {
        color: var(--icon-color);
        width: 14px;
        height: 14px;
      }
    }

    .event-card-desc {
      color: var(--terciary-text-color);
      font-size: 14px;
      line-height: 1.4;
      margin-bottom: 8px;
    }

    .event-card-footer {
      display: flex;
      align-items: center;
      color: var(--secondary-text-color);
      font-size: 12px;

      svg {
        color: var(--icon-color);
        width: 14px;
        height: 14px;
      }
    }
  }

  // Week View Styles
  .week-view {
    .week-navigation {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding: 0 10px;

      .nav-button {
        background: var(--primary-color);
        color: var(--white-bg);
        border: none;
        border-radius: 6px;
        padding: 8px 16px;
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.2s ease;

        &:hover {
          background: var(--primary-color-active);
        }

        &:disabled {
          background: var(--light-gray);
          cursor: not-allowed;
        }
      }

      .week-range {
        font-size: 18px;
        font-weight: 600;
        color: var(--primary-text-color);
      }
    }

    .week-grid {
      display: grid;
      grid-template-columns: 80px repeat(7, 1fr);
      border: 1px solid var(--border-color);
      background: var(--white-bg);

      .time-column {
        border-right: 1px solid var(--border-color);
        background: var(--light-gray);

        .time-header {
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: var(--primary-text-color);
          border-bottom: 1px solid var(--border-color);
        }

        .time-slot {
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          color: var(--secondary-text-color);
          border-bottom: 1px solid var(--border-color);
        }
      }

      .day-column {
        border-right: 1px solid var(--border-color);

        &:last-child {
          border-right: none;
        }

        .day-header {
          height: 60px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: var(--primary-text-color);
          border-bottom: 1px solid var(--border-color);
          background: var(--background-color);

          .day-name {
            font-size: 12px;
            text-transform: uppercase;
            margin-bottom: 4px;
          }

          .day-number {
            font-size: 16px;

            &.today {
              background: var(--primary-color);
              color: var(--white-bg);
              border-radius: 50%;
              width: 28px;
              height: 28px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }
        }

        .day-hour-slot {
          height: 40px;
          border-bottom: 1px solid var(--border-color);
          position: relative;
          cursor: pointer;
          transition: background-color 0.1s ease;

          &:hover {
            background: var(--light-gray);
          }

          .week-event {
            background: var(--primary-color);
            color: var(--white-bg);
            padding: 2px 4px;
            border-radius: 3px;
            font-size: 10px;
            cursor: pointer;
            z-index: 1;
            margin: 1px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            position: relative;

            &:hover {
              opacity: 0.9;
            }

            .event-time-in-slot {
              font-size: 8px;
              opacity: 0.8;
              margin-top: 1px;
            }
          }
        }
      }
    }
  }

  // Day View Styles
  .day-view {
    .day-header-section {
      margin-bottom: 20px;
    }
    
    .day-navigation {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 10px;
      
      h4 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: var(--primary-text-color);
      }

      .nav-button {
        background: var(--primary-color);
        color: var(--white-bg);
        border: none;
        border-radius: 6px;
        padding: 8px 16px;
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.2s ease;

        &:hover {
          background: var(--primary-color-active);
        }

        &:disabled {
          background: var(--light-gray);
          cursor: not-allowed;
        }
      }

      .current-day {
        font-size: 18px;
        font-weight: 600;
        color: var(--primary-text-color);
      }
    }

    .day-timeline {
      display: grid;
      grid-template-columns: 80px 1fr;
      border: 1px solid var(--border-color);
      background: var(--white-bg);
      max-height: 600px;
      overflow-y: auto;

      .timeline-hours {
        border-right: 1px solid var(--border-color);
        background: var(--light-gray);

        .hour-block {
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          color: var(--secondary-text-color);
          border-bottom: 1px solid var(--border-color);
          font-weight: 500;
        }
      }

      .timeline-content {
        .hour-slot {
          height: 60px;
          border-bottom: 1px solid var(--border-color);
          position: relative;
          cursor: pointer;
          transition: background-color 0.1s ease;

          &:hover {
            background: var(--light-gray);
          }

          .day-event {
            position: absolute;
            background: var(--primary-color);
            color: var(--white-bg);
            padding: 4px 8px;
            margin: 2px;
            border-radius: 4px;
            font-size: 11px;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            transition: all 0.2s ease;
            left: 2px;
            right: 2px;
            top: 2px;
            overflow: hidden;
            
            &:hover {
              opacity: 0.9;
              transform: translateY(-1px);
              box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
            }

            .event-time {
              font-size: 9px;
              opacity: 0.9;
              display: block;
              margin-bottom: 2px;
              font-weight: 400;
            }

            .event-title {
              font-weight: 500;
              line-height: 1.2;
              font-size: 11px;
            }
            
            .event-desc {
              font-size: 9px;
              opacity: 0.8;
              margin-top: 2px;
              line-height: 1.1;
            }
          }
        }
      }
    }
  }

  // List View Styles
  .list-view {
    .list-navigation {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding: 0 10px;

      .nav-button {
        background: var(--primary-color);
        color: var(--white-bg);
        border: none;
        border-radius: 6px;
        padding: 8px 16px;
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.2s ease;

        &:hover {
          background: var(--primary-color-active);
        }
      }

      .current-month {
        font-size: 18px;
        font-weight: 600;
        color: var(--primary-text-color);
      }
    }

    .calendar-events-list {
      background: var(--white-bg);
      border: 1px solid var(--border-color);
      border-radius: 8px;
      overflow: hidden;

      .event-list-item {
        padding: 16px;
        border-bottom: 1px solid var(--border-color);
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
          background: var(--light-gray);
        }

        &:last-child {
          border-bottom: none;
        }

        .event-date {
          font-size: 12px;
          color: var(--secondary-text-color);
          margin-bottom: 4px;
        }

        .event-title {
          font-weight: 600;
          color: var(--primary-text-color);
          margin-bottom: 4px;
        }

        .event-description {
          font-size: 14px;
          color: var(--terciary-text-color);
        }
      }

      .no-events {
        padding: 40px;
        text-align: center;
        color: var(--secondary-text-color);
      }
    }
  }

  main {
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;

    header {
      padding: 0;
      height: $width-main-header;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      box-shadow: none;
      background-color: unset;

      .month-year {
        padding-left: 16px;
        width: 150px;
        display: inline-block;
      }

      .chevron {
        cursor: pointer;
      }
      .chevron:hover{
        color: var(--light-gray);
      }

      .header-actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 5px;

        .keyboard-shortcuts {
          opacity: 0.7;
          font-size: 11px;
          cursor: help;
          
          &:hover {
            opacity: 1;
          }
        }

        .action-buttons {
          display: flex;
          align-items: center;
          gap: 10px;

          .btn {
            display: flex;
            align-items: center;

            svg {
              flex-shrink: 0;
            }
          }
        }
      }
    }

    .calendar-table {
      width: 100%;
      height: calc(100% - #{$width-main-header});

      display: flex;
      flex-direction: column;
      background-color: var(--white-bg);
    }

    .row-week {
      position: relative;
      flex-basis: 100%;

      display: flex;
      flex-direction: row;
    }

    .event-header {
      height: $width-event-header;
    }

    .event-container {
      height: calc(100% - #{$width-event-header});
      overflow-y: auto;
      margin: 1.5em;
      padding: 0 12px;
    }

    .day-week {
      flex-basis: 100%;
      border: 1px solid var(--border-color);
      min-height: 120px;
    }

    .calendar-weekdays {
      border-bottom: 2px solid var(--border-color);
      background: var(--background-tab);

      .day-week-header {
        flex-basis: 100%;
        padding: 12px 8px;
        text-align: center;
        font-weight: 600;
        font-size: 14px;
        color: var(--primary-text-color);
        border-right: 1px solid var(--border-color);

        &:last-child {
          border-right: none;
        }
      }
    }

    .borderless-week .day-week{
      border: none;
      text-align: center;
    }

    .centralize{
      display: flex;
      justify-content: center;
      align-items: center;
    }

  }

  // Estilos para as novas visualizações
  .coming-soon {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: var(--secondary-text-color);

    h3 {
      color: var(--primary-text-color);
      margin-bottom: 10px;
    }
  }

  .list-view {
    padding: 20px;
    background: var(--white-bg);

    .events-list {
      max-width: 800px;
      margin: 0 auto;
    }

    .event-list-item {
      padding: 15px;
      margin-bottom: 15px;
      border: 1px solid var(--border-color);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
      background: var(--white-bg);

      &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
      }

      .event-list-date {
        color: var(--secondary-text-color);
        font-size: 14px;
        margin-bottom: 8px;
      }

      .event-list-title {
        font-size: 18px;
        color: var(--primary-text-color);
        margin-bottom: 8px;
      }

      .event-list-desc {
        color: var(--terciary-text-color);
        font-size: 14px;
        line-height: 1.5;
      }

      hr {
        margin-top: 15px;
        border: none;
        border-top: 1px solid var(--border-color);
      }
    }
  }

  .week-view, .day-view {
    min-height: 500px;
    background: var(--white-bg);
    padding: 20px;
  }

  // Media queries para responsividade
  @media (max-width: 768px) {
    .events-tab {
      padding: 10px;
      
      .event-container {
        grid-template-columns: 1fr;
      }
    }
  }

  @media print {
    .no-print {
      display: none !important;
    }

    .events-tab .filters-section {
      display: none;
    }
  }

</style>
